import { useCallback } from "react";
import { useMyLibrariesQuery, useMyProjectsQuery } from "@/generated/graphql";
import { trackEvent } from "@/lib/analytics/trackEvent";

/**
 * Returns a callback that fires the `project_created` dataLayer event with an
 * `is_first` flag derived from the pre-creation project/library count.
 *
 * Call the callback right after the create mutation succeeds and BEFORE any
 * refetch of MyProjects/MyLibraries — the flag reads the pre-creation cache.
 */
export const useTrackProjectCreated = (
  mode: "project" | "library" = "project",
  enabled = true,
) => {
  const { data: projectsData } = useMyProjectsQuery({
    variables: { limit: 1, offset: 0 },
    skip: !enabled || mode !== "project",
    fetchPolicy: "cache-first",
  });
  const { data: librariesData } = useMyLibrariesQuery({
    variables: { limit: 1, offset: 0 },
    skip: !enabled || mode !== "library",
    fetchPolicy: "cache-first",
  });

  return useCallback(() => {
    const existing =
      mode === "library"
        ? librariesData?.myLibraries?.length
        : projectsData?.myProjects?.length;
    trackEvent("project_created", {
      is_first: existing === 0,
      mode,
    });
  }, [mode, projectsData, librariesData]);
};
